import { DEFAULT_TIME_ZONE, formatNumber, NO_VALUE, zonedParts } from '@/features/constructor/engine';

/**
 * Date / number formats of the device registry (03-modules §2.7):
 *   registry "Yaratilgan vaqti", "Batafsil" modal → `DD MMMM YYYY "yil" HH:mm`  ("04 aprel 2026 yil 15:04")
 *   physical / virtual readings                 → `DD.MM.YYYY HH:mm`             ("02.04.2026 10:53")
 *   device values                               → decimal comma                  ("10,6", "915,0")
 * Months are spelled explicitly (Intl's Uzbek output is not reliable). Times are in the reservoir zone.
 */

const MONTHS: Record<string, readonly string[]> = {
  'uz-Latn': ['yanvar', 'fevral', 'mart', 'aprel', 'may', 'iyun', 'iyul', 'avgust', 'sentabr', 'oktabr', 'noyabr', 'dekabr'],
  'uz-Cyrl': ['январ', 'феврал', 'март', 'апрел', 'май', 'июн', 'июл', 'август', 'сентябр', 'октябр', 'ноябр', 'декабр'],
  ru: ['января', 'февраля', 'марта', 'апреля', 'мая', 'июня', 'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря'],
  en: ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'],
};
/** Word after the year: "2026 yil", "2026 йил", "2026 г." */
const YEAR_WORD: Record<string, string> = { 'uz-Latn': ' yil', 'uz-Cyrl': ' йил', ru: ' г.', en: '' };

const pad = (n: number) => String(n).padStart(2, '0');
const toMs = (ts: string | number | Date) => (ts instanceof Date ? ts.getTime() : typeof ts === 'number' ? ts : Date.parse(ts));

/** "04 aprel 2026 yil 15:04" */
export function formatRegistryDate(ts: string | number | Date | null | undefined, locale = 'uz-Latn', timeZone = DEFAULT_TIME_ZONE) {
  if (ts == null) return NO_VALUE;
  const ms = toMs(ts);
  if (!Number.isFinite(ms)) return NO_VALUE;
  const p = zonedParts(ms, timeZone);
  const months = MONTHS[locale] ?? MONTHS['uz-Latn']!;
  return `${pad(p.day)} ${months[p.month - 1]} ${p.year}${YEAR_WORD[locale] ?? ''} ${pad(p.hour)}:${pad(p.minute)}`;
}

/** "02.04.2026 10:53" */
export function formatReadingTime(ts: string | number | Date | null | undefined, timeZone = DEFAULT_TIME_ZONE) {
  if (ts == null) return NO_VALUE;
  const ms = toMs(ts);
  if (!Number.isFinite(ms)) return NO_VALUE;
  const p = zonedParts(ms, timeZone);
  return `${pad(p.day)}.${pad(p.month)}.${p.year} ${pad(p.hour)}:${pad(p.minute)}`;
}

/** "20.04.2026" */
export function formatShortDate(ts: string | number | Date | null | undefined, timeZone = DEFAULT_TIME_ZONE) {
  if (ts == null) return NO_VALUE;
  const ms = toMs(ts);
  if (!Number.isFinite(ms)) return NO_VALUE;
  const p = zonedParts(ms, timeZone);
  return `${pad(p.day)}.${pad(p.month)}.${p.year}`;
}

/** Device readings use a decimal comma in Uzbek and Russian ("10,6"), a point in English. */
export function formatReading(value: number | null | undefined, decimals: number | null | undefined, locale = 'uz-Latn') {
  const text = formatNumber(value, { decimals, locale: 'en' });
  return locale.startsWith('en') ? text : text.replace('.', ',');
}

/** "1.2 MB", "23.0 KB" (sizes use a point, §2.7). */
export function formatFileSize(bytes: number) {
  if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
  if (bytes >= 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${bytes} B`;
}

/** Machine code from a display name: "Blok P - 1 bosimi" → "blok-p-1-bosimi" (device / indicator codes are required). */
export function slugCode(name: string, fallback = 'code') {
  const map: Record<string, string> = { "'": '', '’': '', 'ʻ': '', 'ʼ': '', '`': '' };
  const ascii = name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/['’ʻʼ`]/g, (c) => map[c] ?? '')
    .toLowerCase()
    .replace(/[^a-z0-9а-яё]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 48);
  return ascii || fallback;
}

/** Case- and apostrophe-insensitive search ("tog'ri" finds "Tog‘ri"). */
export function matches(haystack: string | null | undefined, needle: string) {
  if (!needle) return true;
  const norm = (s: string) => s.toLocaleLowerCase('uz').replace(/[’ʻʼ`‘]/g, "'");
  return norm(haystack ?? '').includes(norm(needle.trim()));
}
